import {
    Scene,
    PerspectiveCamera,
    WebGLRenderer,
    Clock,
    PlaneGeometry,
    ShaderMaterial,
    Mesh,
    TextureLoader,
    DoubleSide
} from "three";
import vertexShader from "../glsl/vertex.glsl";
import fragmentShader from "../glsl/fragment.glsl";

interface WaveOptions {
    rootElement: HTMLElement;
    width: number;
    height: number;
    imgSrc: string;
}

export default class Wavy {
    private readonly scene: Scene;
    private readonly camera: PerspectiveCamera;
    private readonly renderer: WebGLRenderer;
    private readonly clock: Clock;
    private geometry?: PlaneGeometry;
    private material?: ShaderMaterial;
    private mesh?: Mesh;

    private options: WaveOptions;
    private ratios: {w: number, h: number};

    constructor(options: WaveOptions) {
        this.options = options;

        this.ratios = this.calculateAspectRatio(this.options.width, this.options.height);

        this.scene = new Scene();
        this.camera = new PerspectiveCamera(
            45,
            window.innerWidth / window.innerHeight,
            0.1,
            10
        );

        this.camera.position.z = 1;

        const canvasElem = document.createElement("canvas");
        canvasElem.width = this.options.width;
        canvasElem.height = this.options.height;
        const canvas = this.options.rootElement.appendChild(canvasElem);

        this.renderer = new WebGLRenderer({
            canvas,
            antialias: true
        });

        this.renderer.setSize(this.options.width, this.options.height);
        this.renderer.setClearColor(0xFFFFFF, 0);

        this.clock = new Clock();

        this.onResize();
    }

    private calculateAspectRatio(w: number, h: number) {
        const aspectRatio = w > h ? { w: 0.8, h: (0.8 * h / w) } : { w: (0.8 * w / h), h: 0.8 };
        return {
            w: parseFloat(aspectRatio.w.toFixed(2)),
            h: parseFloat(aspectRatio.h.toFixed(2))
        };
    }

    init() {
        this.createMesh();
        this.addEvents();
    }

    createMesh() {
        this.geometry = new PlaneGeometry(this.ratios.w, this.ratios.h, 16, 16);
        this.material = new ShaderMaterial({
            vertexShader,
            fragmentShader,
            uniforms: {
                uTime: { value: 0.0 },
                uTexture: { value: new TextureLoader().load(this.options.imgSrc) }
            },
            side: DoubleSide
        });
        this.mesh = new Mesh(this.geometry, this.material);
        this.scene.add(this.mesh);
    }

    addEvents() {
        window.requestAnimationFrame(this.run.bind(this));
        window.addEventListener("resize", this.onResize.bind(this), false);
    }

    run() {
        requestAnimationFrame(this.run.bind(this));
        this.render();
    }

    render() {
        this.material!.uniforms.uTime.value = this.clock.getElapsedTime();
        this.renderer.render(this.scene, this.camera);
    }

    onResize() {
        const w = this.options.width;
        const h = this.options.height;

        this.camera.aspect = w / h;
        this.camera.updateProjectionMatrix();

        this.renderer.setSize(w, h);
    }
}
